// The website's visits, page views and named clicks, reported to the backend (POST /api/nav, which keeps them in
// growth.visits and growth.visit_events; promptchan-backend docs/reference/visit-tracking.md). On by default (user
// decision 2026-10-02: no variable to set); NEXT_PUBLIC_VISIT_TRACKING='false' on Vercel, and a redeploy, turns it off.
// <VisitTracker /> drives it.
//
// Two kinds of visitor, by the site's consent rule (consent.js):
//   - one who allowed analytics: the visit carries their visitor id (visitor-id.js) and lasts until 30 minutes pass
//     without activity or a new link brings them in (a referring site, utm tags, an ad click id, a ?ref= code);
//     its page views and its clicks on named things are reported in batches.
//   - one who has not (EU, EEA, UK, before accepting the banner): one report per arrival, with no id, held in memory
//     only, and nothing else. If they accept during it, the visit goes on under the same id, now with theirs.
//
// What is named: an element's data-track ("upgrade.card_pro"), the landing page's data-landing-action
// ("landing.signup"), or a link to one of the app's main routes ("nav.upgrade"). Nothing else about a click is kept:
// no text, no position, no form values.
import Cookies from 'js-cookie'
import { CLICK_IDS, OWN_HOST, tag } from './entry-source.js'
import {
  VISITOR_COOKIE,
  VISITOR_MAX_AGE,
  newVisitor,
  parseVisitorCookie,
  visitorCookieValue,
} from './visitor-id.js'

export const VISIT_TRACKING = process.env.NEXT_PUBLIC_VISIT_TRACKING !== 'false'
export const IDLE_MS = 30 * 60 * 1000
const BATCH = 25 // events a body; the backend takes at most 50
const MAX_QUEUE = 200 // events held while the backend is away; the oldest go first
const FLUSH_MS = 2000
const VISIT_KEY = 'pc_visit' // localStorage: the visit, shared by the browser's tabs
const LINKED_KEY = 'pc_vid_linked'
const PATH = /^\/[!-~]*$/
const HOST = /^[a-z0-9-]+(\.[a-z0-9-]+)*$/
const REF = /^[A-Za-z0-9._~+-]{1,100}$/
const ACTION = /^[a-z0-9_]{1,40}(\.[a-z0-9_]{1,40}){0,3}$/
const LANGUAGE = /^[A-Za-z]{2,3}(-[A-Za-z0-9]{1,8})*$/
// Sites that hand a visitor back in the middle of a visit (sign-in, payment): not a new arrival from them.
const RETURN_HOSTS =
  /(^|\.)(accounts\.google\.com|appleid\.apple\.com|firebaseapp\.com|centrobill\.com|nowpayments\.io|g2a\.com)$/
// The app's main routes: a link to one is named for it (nav.<route>) without tagging every link.
export const NAV_ROUTES = [
  'signup',
  'upgrade',
  'gems',
  'generate',
  'explore',
  'chat',
  'earn-gems',
  'articles',
]

// ----------------------------------------------------------------------------------- pure: what a page and a click are

/** A path as the backend keeps it (no query, at most 200 characters), or null. */
export function cleanPath(path) {
  return typeof path === 'string' &&
    path.length <= 200 &&
    PATH.test(path) &&
    !/[?#]/.test(path)
    ? path
    : null
}

/**
 * Where this page load came from, if it is an arrival: { landing_path, referrer_host?, utm_source?, utm_medium?,
 * utm_campaign?, utm_term?, utm_content?, click_id?, ref_code? }, the same values as entry-source.js records. Null for
 * a reload, a move back or forward, or a page opened from one of our own (or from a sign-in or payment page).
 */
export function arrival(url, referrer, navigationType) {
  if (navigationType === 'reload' || navigationType === 'back_forward')
    return null
  let host
  try {
    if (referrer) host = new URL(referrer).hostname.toLowerCase()
  } catch {
    /* an unreadable referrer is no source */
  }
  if (
    host &&
    (OWN_HOST.test(host) || host === url.hostname || RETURN_HOSTS.test(host))
  )
    return null
  const found = { landing_path: cleanPath(url.pathname) || '/' }
  if (host && host.length <= 253 && HOST.test(host)) found.referrer_host = host
  const params = new Map(
    [...url.searchParams].map(([key, value]) => [key.toLowerCase(), value]),
  )
  for (const key of [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
  ]) {
    const value = tag(params.get(key))
    if (value) found[key] = value
  }
  const clickId = CLICK_IDS.find((key) => params.has(key))
  if (clickId) found.click_id = clickId
  const ref = params.get('ref')
  if (ref && REF.test(ref)) found.ref_code = ref
  return found
}

/** Whether an arrival names where it came from, which starts a new visit even inside a running one. */
export function hasSource(found) {
  return Boolean(
    found &&
    (found.referrer_host ||
      found.utm_source ||
      found.utm_medium ||
      found.utm_campaign ||
      found.click_id ||
      found.ref_code),
  )
}

/** A click's name as the backend keeps it, or null. */
export function actionName(raw) {
  const name = String(raw ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9_.]+/g, '_')
    .replace(/^[_.]+|[_.]+$/g, '')
  return ACTION.test(name) ? name : null
}

/** The name of what was clicked: its data-track, the landing page's action, or a link to a main route; else null. */
export function clickName(target, origin) {
  const tagged = target?.closest?.('[data-track]')
  if (tagged) return actionName(tagged.dataset.track)
  const landing = target?.closest?.('[data-landing-action]')
  if (landing) return actionName(`landing.${landing.dataset.landingAction}`)
  const anchor = target?.closest?.('a[href]')
  if (!anchor) return null
  let url
  try {
    url = new URL(anchor.getAttribute('href'), origin)
  } catch {
    return null
  }
  if (url.origin !== origin) return null
  const route = url.pathname.split('/')[1] || ''
  if (route === 'signup' && url.searchParams.get('mode') === 'login')
    return 'nav.login'
  return NAV_ROUTES.includes(route) ? actionName(`nav.${route}`) : null
}

/** The visit a page continues, or a new one: { visit, isNew }. `stored` is { header, last } or null. */
export function continueOrBegin({
  stored,
  found,
  path,
  visitor,
  anonymousId,
  now,
  language,
}) {
  const fresh = stored?.header?.id && now - stored.last < IDLE_MS
  if (fresh && !hasSource(found)) {
    return {
      visit: {
        ...stored.header,
        visitor_id: stored.header.visitor_id || visitor.id,
      },
      isNew: false,
    }
  }
  const visit = {
    id: anonymousId || crypto.randomUUID(),
    visitor_id: visitor.id,
    ...(found || { landing_path: path }),
    returning: visitor.at * 1000 < now - IDLE_MS,
  }
  if (language && language.length <= 35 && LANGUAGE.test(language))
    visit.language = language
  return { visit, isNew: true }
}

// ----------------------------------------------------------------------------------- the browser

const state = {
  queue: [],
  visit: null,
  anonymous: null,
  found: undefined,
  foundUsed: false,
  timer: null,
  sending: false,
}

function storage() {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

function readStored() {
  try {
    return JSON.parse(storage()?.getItem(VISIT_KEY) || 'null')
  } catch {
    return null
  }
}

function writeStored(visit, last) {
  try {
    storage()?.setItem(VISIT_KEY, JSON.stringify({ header: visit, last }))
  } catch {
    /* memory is enough */
  }
}

function pageArrival() {
  if (state.found === undefined) {
    let type
    try {
      type = performance.getEntriesByType('navigation')[0]?.type
    } catch {
      /* older browsers */
    }
    state.found = arrival(
      new URL(window.location.href),
      document.referrer,
      type,
    )
  }
  return state.found
}

function visitor() {
  let found = parseVisitorCookie(Cookies.get(VISITOR_COOKIE))
  if (!found) {
    // the middleware sets it on the next page load; until then the page's own, which it turns into a server cookie
    found = newVisitor()
    Cookies.set(VISITOR_COOKIE, visitorCookieValue(found), {
      path: '/',
      sameSite: 'lax',
      secure: window.location.protocol === 'https:',
      expires: new Date((found.at + VISITOR_MAX_AGE) * 1000),
    })
  }
  return found
}

/** The current visit for a visitor who allowed analytics, beginning one when it is time. */
function currentVisit(path, now = Date.now()) {
  const found = state.foundUsed ? null : pageArrival()
  const { visit } = continueOrBegin({
    stored: readStored(),
    found,
    path,
    visitor: visitor(),
    anonymousId: state.anonymous?.id,
    now,
    language: navigator.language,
  })
  state.foundUsed = true
  state.anonymous = null
  state.visit = visit
  writeStored(visit, now)
  return visit
}

/** A page was shown. `allowed`: the visitor allowed analytics. */
export function recordPage(path, allowed) {
  if (!VISIT_TRACKING || navigator.webdriver) return
  const clean = cleanPath(path)
  if (!allowed) {
    // one report per arrival, nothing else; kept in memory so accepting later continues this visit
    const found = pageArrival()
    if (found && !state.anonymous && !state.visit) {
      state.anonymous = { id: crypto.randomUUID(), visitor_id: null, ...found }
      send({ visit: state.anonymous, events: [] })
    }
    return
  }
  currentVisit(clean || '/')
  if (clean) queue({ kind: 'page_view', path: clean })
}

/** Something was clicked; recorded if it has a name and the visitor allowed analytics. */
export function recordClick(target, allowed) {
  if (!VISIT_TRACKING || !allowed || navigator.webdriver) return
  const action = clickName(target, window.location.origin)
  const path = cleanPath(window.location.pathname)
  if (!action || !path) return
  currentVisit(path)
  queue({ kind: 'click', path, action })
  // a link may leave the page at once: send now
  if (target?.closest?.('a[href]')) flush(true)
}

function queue(event) {
  state.queue.push({ id: crypto.randomUUID(), ...event, at: Date.now() })
  if (state.queue.length > MAX_QUEUE)
    state.queue.splice(0, state.queue.length - MAX_QUEUE)
  clearTimeout(state.timer)
  state.timer = setTimeout(() => flush(), FLUSH_MS)
}

/** Send what is queued. `leaving`: the page may be closing, so hand it to the browser to deliver (sendBeacon). */
export async function flush(leaving = false) {
  clearTimeout(state.timer)
  if (!state.visit || !state.queue.length || (state.sending && !leaving)) return
  const events = state.queue.slice(0, BATCH)
  const now = Date.now()
  const body = {
    visit: state.visit,
    events: events.map(({ at, ...event }) => ({
      ...event,
      age_ms: Math.max(0, now - at),
    })),
  }
  state.sending = true
  const sent = await send(body, leaving)
  state.sending = false
  if (sent) {
    const ids = new Set(events.map((event) => event.id))
    state.queue = state.queue.filter((event) => !ids.has(event.id))
    if (state.queue.length) flush(leaving)
  }
}

async function send(body, leaving = false) {
  const url = `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/nav`
  const json = JSON.stringify(body)
  // text/plain: no preflight, and a page that is closing can still send it
  if (leaving && navigator.sendBeacon) {
    try {
      if (navigator.sendBeacon(url, json)) return true
    } catch {
      /* fall through to fetch */
    }
  }
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: json,
      keepalive: true,
      signal: AbortSignal.timeout(10000),
    })
    return response.ok || response.status === 400 || response.status === 413 // a refusal is not retried
  } catch {
    return false
  }
}

/** Tie this browser to the signed-in account, once per browser and account. */
export async function linkAccount(user, allowed) {
  if (!VISIT_TRACKING || !allowed || !user) return
  const found = parseVisitorCookie(Cookies.get(VISITOR_COOKIE))
  if (!found) return
  const key = `${found.id}:${user.uid}`
  try {
    if (storage()?.getItem(LINKED_KEY) === key) return
  } catch {
    /* send again */
  }
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/nav/link`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: await user.getIdToken(),
        },
        body: JSON.stringify({
          visitor_id: found.id,
          visit_id: state.visit?.id,
        }),
        signal: AbortSignal.timeout(10000),
      },
    )
    if (response.ok) storage()?.setItem(LINKED_KEY, key)
  } catch {
    /* next page load tries again */
  }
}

/** The visitor withdrew: stop, and forget the visit and the visitor id on this browser. */
export function forget() {
  state.queue = []
  state.visit = null
  clearTimeout(state.timer)
  Cookies.remove(VISITOR_COOKIE, { path: '/' })
  try {
    storage()?.removeItem(VISIT_KEY)
    storage()?.removeItem(LINKED_KEY)
  } catch {
    /* nothing kept */
  }
}
